import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Star, Users, TrendingUp, ThumbsUp, Clock, AlertOctagon, CheckCircle2, Timer, Download, FilterX, CalendarDays } from "lucide-react";
import { ModuleHeaderIcon } from "@/components/ModuleHeader";
import { API } from "@/App";

const StarRow = ({ value }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        className={`w-3.5 h-3.5 ${n <= Math.round(value) ? "text-amber-400 fill-amber-400" : "text-slate-200"}`}
      />
    ))}
    <span className="ml-2 text-xs text-slate-500">{value.toFixed(1)}</span>
  </div>
);

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }) +
      " " + d.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
};

const StatCard = ({ icon: Icon, label, value, color = "blue", hint }) => (
  <Card>
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`w-11 h-11 rounded-lg flex items-center justify-center bg-${color}-50 flex-shrink-0`}>
        <Icon className={`w-5 h-5 text-${color}-600`} />
      </div>
      <div className="min-w-0">
        <p className="text-2xl font-bold text-slate-800 truncate">{value}</p>
        <p className="text-xs text-slate-500 truncate">{label}</p>
        {hint && <p className="text-[10px] text-slate-400 mt-0.5">{hint}</p>}
      </div>
    </CardContent>
  </Card>
);

const formatDuration = (hours) => {
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 24) return `${hours.toFixed(1)} h`;
  return `${(hours / 24).toFixed(1)} días`;
};

const HelpdeskMetricas = () => {
  const [survey, setSurvey] = useState(null);
  const [sla, setSla] = useState(null);
  const [activeTab, setActiveTab] = useState("sla");
  const [agents, setAgents] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [filters, setFilters] = useState({ agent_id: "", departamento: "", category: "", date_from: "", date_to: "" });

  const fetchData = useCallback(() => {
    const params = new URLSearchParams();
    if (filters.agent_id) params.append("agent_id", filters.agent_id);
    if (filters.departamento) params.append("departamento", filters.departamento);
    if (filters.category) params.append("category", filters.category);
    if (filters.date_from) params.append("date_from", filters.date_from);
    if (filters.date_to) params.append("date_to", filters.date_to);
    const qs = params.toString() ? `?${params.toString()}` : "";
    axios.get(`${API}/helpdesk/survey-metrics${qs}`, { withCredentials: true }).then((r) => setSurvey(r.data)).catch(() => {});
    axios.get(`${API}/helpdesk/sla-metrics${qs}`, { withCredentials: true }).then((r) => setSla(r.data)).catch(() => {});
  }, [filters]);

  useEffect(() => {
    // Load filter options once
    axios.get(`${API}/users-list`, { withCredentials: true }).then(r => setAgents(r.data.filter(e => e.is_active !== false))).catch(() => {});
    axios.get(`${API}/departamentos-operativos`, { withCredentials: true }).then(r => setDepartamentos(r.data.map(d => d.nombre))).catch(() => {});
    axios.get(`${API}/ticket-categorias`, { withCredentials: true }).then(r => setCategorias(r.data.map(c => c.nombre))).catch(() => {});
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const hasFilters = filters.agent_id || filters.departamento || filters.category || filters.date_from || filters.date_to;
  const clearFilters = () => setFilters({ agent_id: "", departamento: "", category: "", date_from: "", date_to: "" });

  const exportExcel = async (kind) => {
    try {
      const params = new URLSearchParams();
      if (filters.agent_id) params.append("agent_id", filters.agent_id);
      if (filters.departamento) params.append("departamento", filters.departamento);
      if (filters.category) params.append("category", filters.category);
      if (filters.date_from) params.append("date_from", filters.date_from);
      if (filters.date_to) params.append("date_to", filters.date_to);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await axios.get(`${API}/helpdesk/${kind}-metrics/export${qs}`, {
        withCredentials: true,
        responseType: "blob",
      });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : `helpdesk_${kind}.xlsx`;
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Reporte descargado");
    } catch {
      toast.error("Error al descargar el reporte");
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="helpdesk-metricas">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <ModuleHeaderIcon icon={TrendingUp} />
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Helpdesk — Métricas</h1>
              <p className="text-slate-500">SLA, productividad y calidad percibida</p>
            </div>
          </div>
          <Button
            onClick={() => exportExcel(activeTab === "sla" ? "sla" : "survey")}
            variant="outline"
            className="border-green-600 text-green-700 hover:bg-green-50"
            data-testid="export-xlsx-btn"
          >
            <Download className="w-4 h-4 mr-2" /> Descargar Excel
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Agente</label>
                <Select value={filters.agent_id || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, agent_id: v === "all" ? "" : v }))}>
                  <SelectTrigger data-testid="filter-agent"><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Departamento</label>
                <Select value={filters.departamento || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, departamento: v === "all" ? "" : v }))}>
                  <SelectTrigger data-testid="filter-dep"><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {departamentos.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Categoría</label>
                <Select value={filters.category || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, category: v === "all" ? "" : v }))}>
                  <SelectTrigger data-testid="filter-cat"><SelectValue placeholder="Todas" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {categorias.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                  <CalendarDays className="w-3 h-3" />
                  {activeTab === "survey" ? "Fecha desde (respuesta)" : "Fecha desde (creación)"}
                </label>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  data-testid="filter-date-from"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                  <CalendarDays className="w-3 h-3" />
                  {activeTab === "survey" ? "Fecha hasta (respuesta)" : "Fecha hasta (creación)"}
                </label>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  data-testid="filter-date-to"
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={clearFilters}
                  disabled={!hasFilters}
                  className="w-full"
                  data-testid="clear-filters-btn"
                >
                  <FilterX className="w-4 h-4 mr-2" /> Limpiar filtros
                </Button>
              </div>
            </div>
            {hasFilters && (
              <div className="mt-2 flex gap-1 flex-wrap">
                {filters.agent_id && <Badge variant="outline" className="text-[10px]">Agente: {agents.find(a => a.id === filters.agent_id)?.name}</Badge>}
                {filters.departamento && <Badge variant="outline" className="text-[10px]">Depto: {filters.departamento}</Badge>}
                {filters.category && <Badge variant="outline" className="text-[10px]">Cat: {filters.category}</Badge>}
                {filters.date_from && <Badge variant="outline" className="text-[10px]">Desde: {filters.date_from}</Badge>}
                {filters.date_to && <Badge variant="outline" className="text-[10px]">Hasta: {filters.date_to}</Badge>}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tabs */}
        <div className="flex gap-2 border-b">
          <button
            type="button"
            onClick={() => setActiveTab("sla")}
            className={`flex items-center gap-2 px-4 py-2 text-sm rounded-t-lg border-b-2 transition-colors ${
              activeTab === "sla"
                ? "border-blue-600 text-blue-700 font-semibold bg-blue-50/50"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid="tab-sla"
          >
            <Timer className="w-4 h-4" /> SLA / Productividad
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("survey")}
            className={`flex items-center gap-2 px-4 py-2 text-sm rounded-t-lg border-b-2 transition-colors ${
              activeTab === "survey"
                ? "border-amber-600 text-amber-700 font-semibold bg-amber-50/50"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid="tab-survey"
          >
            <Star className="w-4 h-4" /> Encuestas de calidad
          </button>
        </div>

        {/* ========== SLA TAB ========== */}
        {activeTab === "sla" && (
          !sla ? <p className="text-slate-400">Cargando...</p> : (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard icon={Users} label="Tickets totales" value={sla.total} color="blue" />
                <StatCard icon={Clock} label="Abiertos" value={sla.open} color="amber" />
                <StatCard icon={CheckCircle2} label="Resueltos" value={sla.closed} color="green" />
                <StatCard icon={AlertOctagon} label="Esperando cliente" value={sla.overdue_count} color="red" hint="Vencidos esperando respuesta" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <StatCard
                  icon={Timer}
                  label="Primera respuesta promedio"
                  value={formatDuration(sla.avg_first_response_hours)}
                  color="purple"
                  hint={`Basado en ${sla.first_response_sample_size} tickets con respuesta interna`}
                />
                <StatCard
                  icon={TrendingUp}
                  label="Ratio cerrados"
                  value={`${sla.total ? Math.round((sla.closed / sla.total) * 100) : 0}%`}
                  color="green"
                />
              </div>

              {/* Reopened tickets section */}
              {sla.reopened && sla.reopened.reopen_events > 0 && (
                <Card className="border-orange-200">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <AlertOctagon className="w-4 h-4 text-orange-600" />
                      Tickets re-abiertos
                    </CardTitle>
                    <p className="text-xs text-slate-500">
                      Tickets que pasaron por el estatus <strong>resuelto</strong> y fueron reabiertos al recibir una nueva respuesta del contacto.
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <StatCard icon={Users} label="Tickets re-abiertos" value={sla.reopened.tickets_reopened} color="orange" />
                      <StatCard icon={Clock} label="Aún abiertos" value={sla.reopened.currently_open} color="red" />
                      <StatCard
                        icon={Timer}
                        label="1ra respuesta tras reapertura"
                        value={formatDuration(sla.reopened.avg_first_response_hours)}
                        color="purple"
                        hint={`${sla.reopened.response_sample_size} muestras`}
                      />
                      <StatCard
                        icon={CheckCircle2}
                        label="Re-resolución promedio"
                        value={formatDuration(sla.reopened.avg_resolution_hours)}
                        color="green"
                        hint={`${sla.reopened.resolution_sample_size} muestras`}
                      />
                    </div>
                    <p className="text-[11px] text-slate-400 mt-3">
                      Eventos totales de reapertura: <strong>{sla.reopened.reopen_events}</strong> (un mismo ticket puede reabrirse varias veces)
                    </p>
                  </CardContent>
                </Card>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Resolution by department */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Timer className="w-4 h-4 text-blue-600" />
                      Tiempo de resolución por departamento
                    </CardTitle>
                    <p className="text-[11px] text-slate-500 mt-1">
                      Tiempo total desde la creación del ticket hasta que el agente lo marcó como resuelto.
                    </p>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Departamento</TableHead>
                          <TableHead>Resueltos</TableHead>
                          <TableHead>Promedio</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sla.resolution_by_departamento.length === 0 ? (
                          <TableRow><TableCell colSpan={3} className="text-center text-slate-400 py-6">Sin tickets resueltos todavía</TableCell></TableRow>
                        ) : sla.resolution_by_departamento.map((r) => (
                          <TableRow key={r.key}>
                            <TableCell className="font-medium">{r.key}</TableCell>
                            <TableCell>{r.count}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="font-mono">{formatDuration(r.avg_hours)}</Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                {/* Tickets waiting for client response (overdue) */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <AlertOctagon className="w-4 h-4 text-red-600" />
                      Esperando cliente (vencidos)
                    </CardTitle>
                    <p className="text-[11px] text-slate-500 mt-1">
                      Tickets donde el <strong>cliente</strong> no respondió antes del vencimiento. Agrupados por el agente responsable para dar seguimiento.
                    </p>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Agente responsable</TableHead>
                          <TableHead>Pendientes</TableHead>
                          <TableHead>Días sin respuesta (avg)</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sla.overdue_by_responsable.length === 0 ? (
                          <TableRow><TableCell colSpan={3} className="text-center text-slate-400 py-6">Ningún ticket esperando respuesta vencida 🎉</TableCell></TableRow>
                        ) : sla.overdue_by_responsable.map((r) => (
                          <TableRow key={r.responsable_id}>
                            <TableCell className="font-medium">{r.responsable_name}</TableCell>
                            <TableCell>
                              <Badge className="bg-red-100 text-red-700">{r.count}</Badge>
                            </TableCell>
                            <TableCell className="text-sm text-slate-600">{r.avg_days_overdue}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>

              {/* Overdue list */}
              {sla.overdue_list.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <AlertOctagon className="w-4 h-4 text-red-600" />
                      Top 30 tickets esperando respuesta del cliente
                    </CardTitle>
                    <p className="text-[11px] text-slate-500">
                      Tickets cuyo vencimiento ya pasó y el cliente no ha respondido. Se auto-cerrarán 24h después del vencimiento.
                    </p>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Folio</TableHead>
                          <TableHead>Asunto</TableHead>
                          <TableHead>Agente</TableHead>
                          <TableHead>Depto</TableHead>
                          <TableHead>Días sin respuesta</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sla.overdue_list.map((t) => (
                          <TableRow key={t.ticket_id} data-testid={`overdue-${t.ticket_id}`}>
                            <TableCell className="font-mono text-xs text-blue-600">{t.folio}</TableCell>
                            <TableCell className="max-w-xs truncate">{t.title}</TableCell>
                            <TableCell className="text-sm">{t.assigned_to_name}</TableCell>
                            <TableCell className="text-sm">{t.departamento || "—"}</TableCell>
                            <TableCell>
                              <Badge className="bg-red-100 text-red-700">{t.days_overdue}d</Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* By category */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Distribución por categoría</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Categoría</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead>Abiertos</TableHead>
                        <TableHead>Cerrados</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sla.by_category.length === 0 ? (
                        <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-6">Sin datos</TableCell></TableRow>
                      ) : sla.by_category.map((r) => (
                        <TableRow key={r.key}>
                          <TableCell className="font-medium">{r.key}</TableCell>
                          <TableCell>{r.count}</TableCell>
                          <TableCell><Badge className="bg-amber-100 text-amber-700">{r.open}</Badge></TableCell>
                          <TableCell><Badge className="bg-green-100 text-green-700">{r.closed}</Badge></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )
        )}

        {/* ========== SURVEY TAB ========== */}
        {activeTab === "survey" && (
          !survey ? <p className="text-slate-400">Cargando...</p> : (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard icon={Users} label="Encuestas respondidas" value={survey.total} color="blue" />
                <StatCard icon={ThumbsUp} label="Resueltos (Sí)" value={`${survey.resolved_pct}%`} color="green" />
                <StatCard icon={Star} label="Respuesta" value={`${survey.avg_response}/5`} color="amber" />
                <StatCard icon={TrendingUp} label="Atención" value={`${survey.avg_attention}/5`} color="purple" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader><CardTitle className="text-base">Por categoría</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Categoría</TableHead>
                          <TableHead>#</TableHead>
                          <TableHead>Respuesta</TableHead>
                          <TableHead>Atención</TableHead>
                          <TableHead>Res.</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {survey.by_category.length === 0 ? (
                          <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-6">Sin datos</TableCell></TableRow>
                        ) : survey.by_category.map((r) => (
                          <TableRow key={r.key}>
                            <TableCell className="font-medium">{r.key}</TableCell>
                            <TableCell>{r.count}</TableCell>
                            <TableCell><StarRow value={r.avg_response} /></TableCell>
                            <TableCell><StarRow value={r.avg_attention} /></TableCell>
                            <TableCell><Badge variant="outline">{r.resolved_pct}%</Badge></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader><CardTitle className="text-base">Por departamento</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Departamento</TableHead>
                          <TableHead>#</TableHead>
                          <TableHead>Respuesta</TableHead>
                          <TableHead>Atención</TableHead>
                          <TableHead>Res.</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {survey.by_departamento.length === 0 ? (
                          <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-6">Sin datos</TableCell></TableRow>
                        ) : survey.by_departamento.map((r) => (
                          <TableRow key={r.key}>
                            <TableCell className="font-medium">{r.key}</TableCell>
                            <TableCell>{r.count}</TableCell>
                            <TableCell><StarRow value={r.avg_response} /></TableCell>
                            <TableCell><StarRow value={r.avg_attention} /></TableCell>
                            <TableCell><Badge variant="outline">{r.resolved_pct}%</Badge></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader><CardTitle className="text-base">Encuestas recientes</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Folio</TableHead>
                        <TableHead>Fecha respuesta</TableHead>
                        <TableHead>Cliente</TableHead>
                        <TableHead>Agente</TableHead>
                        <TableHead>Depto</TableHead>
                        <TableHead>Resuelto</TableHead>
                        <TableHead>Respuesta</TableHead>
                        <TableHead>Atención</TableHead>
                        <TableHead>Comentario</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {survey.recent.length === 0 ? (
                        <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-6">Sin encuestas aún</TableCell></TableRow>
                      ) : survey.recent.map((t) => (
                        <TableRow key={t.id} data-testid={`metric-row-${t.id}`}>
                          <TableCell className="font-mono text-xs text-blue-600">{t.folio}</TableCell>
                          <TableCell className="text-xs text-slate-600 whitespace-nowrap" data-testid={`survey-date-${t.id}`}>
                            {formatDate(t.survey.submitted_at)}
                          </TableCell>
                          <TableCell className="text-sm">{t.created_by_name}</TableCell>
                          <TableCell className="text-sm">{t.assigned_to_name || "—"}</TableCell>
                          <TableCell className="text-sm">{t.departamento || "—"}</TableCell>
                          <TableCell>
                            {t.survey.resolved ? (
                              <Badge className="bg-green-100 text-green-700">Sí</Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-700">No</Badge>
                            )}
                          </TableCell>
                          <TableCell><StarRow value={t.survey.response_rating} /></TableCell>
                          <TableCell><StarRow value={t.survey.attention_rating} /></TableCell>
                          <TableCell className="text-xs text-slate-600 max-w-xs truncate" title={t.survey.comments}>
                            {t.survey.comments || "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default HelpdeskMetricas;
